/**
 * Auth0 Callback Handler Component
 * Handles the Auth0 callback after login/signup
 */

import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const CallbackHandler: React.FC = () => {
  const { isAuthenticated, isLoading, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // The AuthContext automatically handles the callback
    // We just need to wait for it to complete and then redirect

    if (!isLoading) {
      if (isAuthenticated) {
        // Check for stored redirect URL
        const redirectUrl = sessionStorage.getItem('auth_redirect_url');
        
        if (redirectUrl && redirectUrl !== '/login' && redirectUrl !== '/callback') {
          sessionStorage.removeItem('auth_redirect_url');
          navigate(redirectUrl, { replace: true });
        } else {
          // Default redirect to dashboard
          navigate('/dashboard', { replace: true });
        }
      } else if (error) {
        // If there's an error, redirect to login with error info
        console.error('Callback error:', error);
        navigate('/login?error=callback_failed', { replace: true });
      }
    }
  }, [isAuthenticated, isLoading, error, navigate]);

  // Show loading state while processing callback
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          Completing sign in...
        </h2>
        <p className="text-gray-600">
          Please wait while we finish setting up your account.
        </p>
        
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700 text-sm">
              There was an issue completing your sign in. You&apos;ll be redirected to try again.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CallbackHandler;